import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { movesApi, stylesApi, skillLevelsApi, instructorsApi, moveTypesApi, handHoldsApi, adminVideosApi, getApiBaseUrl } from '../services/api';
import { Move, CreateMoveData, Style, SkillLevel, Instructor, MoveType, HandHold } from '../types';

const MovesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingMove, setEditingMove] = useState<Move | null>(null);
  const [search, setSearch] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [skillLevelFilter, setSkillLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: movesData, isLoading } = useQuery({
    queryKey: ['moves', { page, search, styleFilter, skillLevelFilter, statusFilter }],
    queryFn: () =>
      movesApi.getAll({
        page,
        limit: 10,
        search: search || undefined,
        styleId: styleFilter ? parseInt(styleFilter) : undefined,
        skillLevelId: skillLevelFilter ? parseInt(skillLevelFilter) : undefined,
        published: statusFilter === 'published' ? true : statusFilter === 'draft' ? false : undefined,
      }),
  });

  const { data: styles = [] } = useQuery({
    queryKey: ['styles-all'],
    queryFn: () => stylesApi.getAll({ limit: 100 }).then(res => res.data),
  });

  const { data: skillLevels = [] } = useQuery({
    queryKey: ['skill-levels-all'],
    queryFn: () => skillLevelsApi.getAll({ limit: 100 }).then(res => res.data),
  });

  const { data: instructors = [] } = useQuery({
    queryKey: ['instructors-all'],
    queryFn: () => instructorsApi.getAll({ limit: 100, status: 'active' }).then(res => res.data),
  });

  const { data: moveTypes = [] } = useQuery({
    queryKey: ['move-types-all'],
    queryFn: () => moveTypesApi.getAll({ limit: 100 }).then(res => res.data),
  });

  const { data: handHolds = [] } = useQuery({
    queryKey: ['hand-holds-all'],
    queryFn: () => handHoldsApi.getAll({ limit: 100 }).then(res => res.data),
  });

  // Get base moves for parent move selection
  const { data: baseMoves = [] } = useQuery({
    queryKey: ['base-moves'],
    queryFn: () => movesApi.getAll({ limit: 1000, published: true }).then(res =>
      res.data.filter(move => move.isBaseMove)
    ),
  });

  const { register, handleSubmit, reset, formState: { errors }, control, setValue } = useForm<CreateMoveData>();

  // Watch the isVariation field to show/hide variation fields
  const isVariation = useWatch({ control, name: 'isVariation' });

  // Watch the parentMoveId to auto-set moveTypeId
  const parentMoveId = useWatch({ control, name: 'parentMoveId' });

  // Auto-set moveTypeId when parent move is selected
  useEffect(() => {
    if (isVariation && parentMoveId && baseMoves.length > 0) {
      const parentMove = baseMoves.find(move => move.id.toString() === parentMoveId.toString());
      if (parentMove) {
        if (parentMove.moveTypeId) {
          setValue('moveTypeId', parentMove.moveTypeId);
        }
        if (parentMove.styleId) {
          setValue('styleId', parentMove.styleId);
        }
      }
    }
  }, [isVariation, parentMoveId, baseMoves, setValue]);

  // Open edit form when navigated with ?edit=<moveId>
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && !editingMove) {
      movesApi.getById(Number(editId)).then((move) => {
        setEditingMove(move);
        setCurrentVideoUrl(move.videoUrl || '');
        setCurrentThumbnailUrl(move.thumbnail || '');
        reset({
          name: move.name,
          description: move.description || '',
          videoUrl: move.videoUrl || '',
          thumbnail: move.thumbnail || '',
          duration: move.duration || 0,
          styleId: move.styleId,
          skillLevelId: move.skillLevelId,
          instructorId: move.instructorId,
          moveTypeId: move.moveTypeId || undefined,
          handHoldId: move.handHoldId || undefined,
          isPublished: move.isPublished,
          accessLevel: move.accessLevel ?? 'free',
          isVariation: !move.isBaseMove,
          parentMoveId: move.parentMoveId || undefined,
          variationDescription: move.variationDescription || '',
        });
        setShowForm(true);
        setSearchParams({}, { replace: true });
      }).catch(() => {
        setSearchParams({}, { replace: true });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const createMutation = useMutation({
    mutationFn: movesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moves'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateMoveData }) =>
      movesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moves'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      setEditingMove(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: movesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moves'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
  });

  // Video upload functions
  const handleVideoUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await adminVideosApi.uploadVideo(file);
      setCurrentVideoUrl(result.videoUrl);
      if (result.thumbnailUrl) {
        setCurrentThumbnailUrl(result.thumbnailUrl);
        setValue('thumbnail', result.thumbnailUrl);
      }
      return result.videoUrl;
    } catch (error: any) {
      console.error('Video upload error:', error);
      alert(error?.response?.data?.error || 'Failed to upload video. Please try again.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleVideoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const videoUrl = await handleVideoUpload(file);
      if (videoUrl) {
        // Update form with new video URL
        setValue('videoUrl', videoUrl);
      }
    }
  };

  const handleThumbnailUpload = async (file: File) => {
    setUploadingThumbnail(true);
    try {
      const result = await adminVideosApi.uploadThumbnail(file);
      setCurrentThumbnailUrl(result.thumbnailUrl);
      setValue('thumbnail', result.thumbnailUrl);
    } catch (error: any) {
      console.error('Thumbnail upload error:', error);
      alert(error?.response?.data?.error || 'Failed to upload thumbnail. Please try again.');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleThumbnailFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleThumbnailUpload(file);
    }
  };

  const onSubmit = (data: CreateMoveData) => {
    const submitData = {
      ...data,
      styleId: parseInt(data.styleId.toString()),
      skillLevelId: parseInt(data.skillLevelId.toString()),
      instructorId: parseInt(data.instructorId.toString()),
      moveTypeId: data.moveTypeId ? parseInt(data.moveTypeId.toString()) : undefined,
      isBaseMove: !data.isVariation, // Inverse of isVariation
      parentMoveId: data.isVariation && data.parentMoveId ? parseInt(data.parentMoveId.toString()) : undefined,
      thumbnail: data.thumbnail || undefined,
      variationDescription: data.isVariation ? data.variationDescription : undefined,
      // Remove isVariation from the final data as the backend uses isBaseMove
      isVariation: undefined,
    };

    if (editingMove) {
      updateMutation.mutate({ id: editingMove.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (move: Move) => {
    setEditingMove(move);
    setCurrentVideoUrl(move.videoUrl || '');
    setCurrentThumbnailUrl(move.thumbnail || '');
    reset({
      name: move.name,
      description: move.description || '',
      videoUrl: move.videoUrl || '',
      thumbnail: move.thumbnail || '',
      duration: move.duration || 0,
      styleId: move.styleId,
      skillLevelId: move.skillLevelId,
      instructorId: move.instructorId,
      moveTypeId: move.moveTypeId || undefined,
      handHoldId: move.handHoldId || undefined,
      isPublished: move.isPublished,
      accessLevel: move.accessLevel ?? 'free',
      isVariation: !move.isBaseMove, // Convert isBaseMove to isVariation
      parentMoveId: move.parentMoveId || undefined,
      variationDescription: move.variationDescription || '',
    });
    setShowForm(true);
  };

  const handleDelete = (move: Move) => {
    if (window.confirm(`Are you sure you want to delete "${move.name}"?`)) {
      deleteMutation.mutate(move.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMove(null);
    setCurrentVideoUrl('');
    setCurrentThumbnailUrl('');
    reset();
  };

  return (
    <div className="moves-management">
      <div className="page-header">
        <h1>Moves Management</h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingMove(null);
            setCurrentThumbnailUrl('');
            reset({
              name: '',
              description: '',
              videoUrl: '',
              thumbnail: '',
              duration: undefined,
              styleId: undefined,
              skillLevelId: undefined,
              instructorId: undefined,
              moveTypeId: undefined,
              handHoldId: undefined,
              isPublished: false,
              accessLevel: 'free',
              isVariation: false,
              parentMoveId: undefined,
              variationDescription: ''
            });
            setShowForm(true);
          }}
          disabled={showForm}
        >
          Add New Move
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingMove ? 'Edit Move' : 'Create New Move'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  type="text"
                  id="name"
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="error-text">{errors.name.message}</span>}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    {...register('isVariation')}
                    type="checkbox"
                    defaultChecked={false}
                  />
                  Variation
                </label>
              </div>
            </div>

            {/* Variation Details Section */}
            {isVariation && (
              <div className="form-section">
                <h3>Variation Details</h3>
                <div className="variation-fields">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="parentMoveId">Parent Move *</label>
                      <select
                        {...register('parentMoveId', {
                          required: isVariation ? 'Parent move is required for variations' : false
                        })}
                        id="parentMoveId"
                        className={errors.parentMoveId ? 'error' : ''}
                      >
                        <option value="">Select the base move this is a variation of</option>
                        {baseMoves.map((move: Move) => (
                          <option key={move.id} value={move.id}>
                            {move.name} ({move.style?.name})
                          </option>
                        ))}
                      </select>
                      {errors.parentMoveId && <span className="error-text">{errors.parentMoveId.message}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="variationDescription">Variation Description *</label>
                      <input
                        {...register('variationDescription', {
                          required: isVariation ? 'Variation description is required' : false
                        })}
                        type="text"
                        id="variationDescription"
                        placeholder="e.g., With Turn, Cross Body, Open Break"
                        className={errors.variationDescription ? 'error' : ''}
                      />
                      {errors.variationDescription && <span className="error-text">{errors.variationDescription.message}</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="styleId">Dance Style *</label>
                <select
                  {...register('styleId', { required: 'Style is required' })}
                  id="styleId"
                  className={errors.styleId ? 'error' : ''}
                >
                  <option value="">Select a style</option>
                  {styles.map((style: Style) => (
                    <option key={style.id} value={style.id}>
                      {style.name}
                    </option>
                  ))}
                </select>
                {errors.styleId && <span className="error-text">{errors.styleId.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="skillLevelId">Skill Level *</label>
                <select
                  {...register('skillLevelId', { required: 'Skill level is required' })}
                  id="skillLevelId"
                  className={errors.skillLevelId ? 'error' : ''}
                >
                  <option value="">Select a skill level</option>
                  {skillLevels.map((level: SkillLevel) => (
                    <option key={level.id} value={level.id}>
                      {level.name} (Level {level.level})
                    </option>
                  ))}
                </select>
                {errors.skillLevelId && <span className="error-text">{errors.skillLevelId.message}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="moveTypeId">Move Type *</label>
                <select
                  {...register('moveTypeId', { required: 'Move type is required' })}
                  id="moveTypeId"
                  className={errors.moveTypeId ? 'error' : ''}
                >
                  <option value="">Select a move type</option>
                  {moveTypes.map((moveType: MoveType) => (
                    <option key={moveType.id} value={moveType.id}>
                      {moveType.name}
                    </option>
                  ))}
                </select>
                {errors.moveTypeId && <span className="error-text">{errors.moveTypeId.message}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="instructorId">Instructor *</label>
                <select
                  {...register('instructorId', { required: 'Instructor is required' })}
                  id="instructorId"
                  className={errors.instructorId ? 'error' : ''}
                >
                  <option value="">Select an instructor</option>
                  {instructors.map((instructor: Instructor) => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </option>
                  ))}
                </select>
                {errors.instructorId && <span className="error-text">{errors.instructorId.message}</span>}
              </div>
            </div>

            {/* Rarely needed — kept out of the way so the form stays short */}
            <details className="form-optional">
              <summary>Optional details</summary>
              <div className="form-group">
                <label htmlFor="handHoldId">Hand Hold</label>
                <select
                  {...register('handHoldId')}
                  id="handHoldId"
                  className={errors.handHoldId ? 'error' : ''}
                >
                  <option value="">None / Open Position</option>
                  {handHolds.map((handHold: HandHold) => (
                    <option key={handHold.id} value={handHold.id}>
                      {handHold.name}
                    </option>
                  ))}
                </select>
                {errors.handHoldId && <span className="error-text">{errors.handHoldId.message}</span>}
              </div>
              <p className="form-optional-hint">
                Manage the lists: <Link to="/admin/hand-holds">hand holds</Link> · <Link to="/admin/positions">positions</Link>
              </p>
            </details>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                placeholder="Describe the move, technique, or steps..."
              />
            </div>

            <div className="form-group">
              <label>Video</label>
              <div className="video-upload-section">
                <div className="video-url-input">
                  <label htmlFor="videoUrl">Video URL (optional)</label>
                  <input
                    {...register('videoUrl')}
                    type="text"
                    id="videoUrl"
                    placeholder="https://youtube.com/watch?v=... or leave empty for default video"
                    onChange={(e) => {
                      setCurrentVideoUrl(e.target.value);
                      setValue('videoUrl', e.target.value);
                    }}
                  />
                </div>

                <div className="video-upload-divider">
                  <span>OR</span>
                </div>

                <div className="video-file-upload">
                  <label htmlFor="videoFile">Upload Video File</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="videoFile"
                    accept="video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/x-ms-wmv"
                    onChange={handleVideoFileChange}
                    disabled={uploading}
                    className="file-input-hidden"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary video-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Choose Video File'}
                  </button>
                  {uploading && (
                    <div className="upload-progress">
                      <span>Uploading video...</span>
                    </div>
                  )}
                </div>

                {currentVideoUrl && (
                  <div className="current-video-preview">
                    <label>Current Video:</label>
                    <div className="video-preview-info">
                      <span>{currentVideoUrl.includes('/api/videos/') ? 'Uploaded file' : 'External URL'}</span>
                      <span className="video-url-display">{currentVideoUrl}</span>
                    </div>
                  </div>
                )}

                <div className="video-help-text">
                  <small>
                    Video is optional. If no video is provided, a default video will be used.
                    Supported formats: MP4, MPEG, QuickTime, AVI, WMV (max 100MB)
                  </small>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Thumbnail</label>
              <div className="video-upload-section">
                {currentThumbnailUrl && (
                  <div className="current-video-preview">
                    <label>Current Thumbnail:</label>
                    <img
                      src={currentThumbnailUrl.startsWith('/') ? `${getApiBaseUrl().replace('/api', '')}${currentThumbnailUrl}` : currentThumbnailUrl}
                      alt="Thumbnail preview"
                      className="move-thumb-preview"
                    />
                  </div>
                )}

                <div className="video-file-upload">
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleThumbnailFileChange}
                    disabled={uploadingThumbnail}
                    className="file-input-hidden"
                    aria-label="Upload thumbnail image"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary video-upload-btn"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                  >
                    {uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}
                  </button>
                  {uploadingThumbnail && (
                    <div className="upload-progress">
                      <span>Uploading thumbnail...</span>
                    </div>
                  )}
                </div>

                <div className="video-help-text">
                  <small>
                    A thumbnail is auto-generated when uploading a video. You can also upload a custom image (JPG, PNG, WebP, max 10MB).
                  </small>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration">Duration (seconds)</label>
                <input
                  {...register('duration', {
                    min: { value: 0, message: 'Duration must be positive' }
                  })}
                  type="number"
                  id="duration"
                  min="0"
                  className={errors.duration ? 'error' : ''}
                />
                {errors.duration && <span className="error-text">{errors.duration.message}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    {...register('isPublished')}
                    type="checkbox"
                    defaultChecked={false}
                  />
                  Published
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="accessLevel">Who can watch</label>
                <select id="accessLevel" {...register('accessLevel')}>
                  <option value="free">Free: all signed-in students</option>
                  <option value="members">Members only: students with a plan</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingMove ? 'Update' : 'Create'} Move
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search moves..."
          aria-label="Search moves"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={styleFilter}
          onChange={(e) => setStyleFilter(e.target.value)}
          className="status-filter"
          aria-label="Filter by style"
        >
          <option value="">All Styles</option>
          {styles.map((style: Style) => (
            <option key={style.id} value={style.id}>
              {style.name}
            </option>
          ))}
        </select>
        <select
          value={skillLevelFilter}
          onChange={(e) => setSkillLevelFilter(e.target.value)}
          className="status-filter"
          aria-label="Filter by skill level"
        >
          <option value="">All Levels</option>
          {skillLevels.map((level: SkillLevel) => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
          aria-label="Filter by status"
        >
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {isLoading ? (
        <div className="loading">Loading moves...</div>
      ) : movesData && movesData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Style</th>
                  <th>Level</th>
                  <th>Instructor</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {movesData.data.map((move) => (
                  <tr key={move.id} className={move.isBaseMove ? 'base-move-row' : 'variation-row'}>
                    <td>
                      <div className={`move-name ${move.isBaseMove ? 'base-move' : 'variation-move'}`}>
                        {!move.isBaseMove && <span className="variation-indent">↳ </span>}
                        <span className="move-type-icon">
                          {move.isBaseMove ? '📋' : '🔄'}
                        </span>
                        <strong>{move.name}</strong>
                        {!move.isBaseMove && move.variationDescription && (
                          <span className="variation-badge">{move.variationDescription}</span>
                        )}
                        <span className="move-type-label">
                          {move.isBaseMove ? '(Base Move)' : '(Variation)'}
                        </span>
                        {move.description && (
                          <div className="move-description-preview">
                            {move.description.length > 80
                              ? `${move.description.substring(0, 80)}...`
                              : move.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{move.style?.name}</td>
                    <td>{move.skillLevel?.name}</td>
                    <td>{move.instructor?.name}</td>
                    <td>{move.duration ? `${move.duration}s` : '-'}</td>
                    <td>
                      <span className={`status ${move.isPublished ? 'published' : 'draft'}`}>
                        {move.isPublished ? 'Published' : 'Draft'}
                      </span>
                      {move.accessLevel === 'members' && <span className="status members-only">Members only</span>}
                    </td>
                    <td>{new Date(move.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleEdit(move)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => handleDelete(move)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {movesData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: movesData.pagination.pages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn btn-small ${
                      pageNum === page ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No moves found. Create your first dance move!</p>
        </div>
      )}
    </div>
  );
};

export default MovesManagement;